import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Box } from '@mui/system';

import { darkThemeOptions, lightThemeOptions, lightWhiteMenuThemeOptions, AppThemeName } from './../themes';

interface IThemeContextData {
  themeName: AppThemeName;
  setThemeName: (name: AppThemeName) => void;
  temaSistema: 'light' | 'dark';
}

const THEME_STORAGE_KEY = 'APP_THEME';
const VALID_THEMES: AppThemeName[] = ['light', 'lightWhiteMenu', 'dark'];

const ThemeContext = createContext({} as IThemeContextData);

export const useAppThemeContext = () => {
  return useContext(ThemeContext);
};

interface IAppThemeProviderProps {
  children: React.ReactNode;
}
export const AppThemeProvider: React.FC<IAppThemeProviderProps> = ({ children }) => {
  // Leitura síncrona do localStorage no inicializador do useState — é isso
  // que evita flash de tema claro no primeiro render quando o usuário já
  // escolheu dark (não trocar por leitura em useEffect).
  const [themeName, setThemeNameState] = useState<AppThemeName>(() => {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as AppThemeName | null;
    return storedTheme && VALID_THEMES.includes(storedTheme) ? storedTheme : 'light';
  });

  const mode: 'light' | 'dark' = themeName === 'dark' ? 'dark' : 'light';

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, themeName);
  }, [themeName]);

  const setThemeName = useCallback((name: AppThemeName) => {
    setThemeNameState(name);
  }, []);

  const theme = useMemo(() => {
    const options =
      themeName === 'dark' ? darkThemeOptions : themeName === 'lightWhiteMenu' ? lightWhiteMenuThemeOptions : lightThemeOptions;

    return createTheme({
      ...options,
      breakpoints: {
        values: {
          xs: 0,
          sm: 700,
          md: 900,
          lg: 1200,
          xl: 1536,
        },
      },
    });
  }, [themeName]);

  return (
    <ThemeContext.Provider value={{ themeName, setThemeName, temaSistema: mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box width="100vw" height="100vh" bgcolor={theme.palette.background.default}>
          {children}
        </Box>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
